import type { EughCaseMatch, RisDocument } from './types'
import { apiFetch } from './api-client'
import type { CourtDecisionCountMap } from './court-counts'

interface SearchBackendDocument {
  eugh?: string[]
  analysisAvailable?: boolean
  volltextNormen?: string[]
  citations?: string[]
  eughContexts?: Record<string, string>
  searchHeading?: string
  excerptLinkedMatch?: boolean
  searchExcerpts?: RisDocument['searchExcerpts']
  searchHighlightTerms?: RisDocument['searchHighlightTerms']
  furtherMentionCount?: number
  excerptStatus?: RisDocument['excerptStatus']
  id: string
  typ: 'RS' | 'TE'
  gz: string
  rechtssatznummer?: string
  gericht: string
  courtDisplayName?: string
  datum: string
  normen: string
  ecli: string
  url: string
  htmlUrl: string
  anmerkung?: string
  reason: string
  searchScore?: number
  schlagworte?: string
  kurzinformation?: string
  entscheidungsart?: string
  geaendert?: string
  linkedFromIds?: string[]
  caseMatch?: {
    type: 'exact' | 'mentioned' | 'partial' | 'possible'
    query: string
    matchedReference?: string
  }
}

interface SearchBackendResponse {
  timedOut?: boolean
  referenceCatalog?: import('../data/gesetze').Gesetz[] | null
  results?: SearchBackendDocument[]
  total?: number
  nextCursor?: string | null
  facets?: Partial<SearchFacets>
  log?: string[]
  otherCourts?: CourtProbe[]
  suggestions?: SearchSuggestion[]
  keywordRelaxed?: string
}

export interface CourtProbe {
  name: string
  applikation: string
  hits: number
}

export type Applikation =
  | 'Justiz' | 'Vfgh' | 'Vwgh' | 'Bvwg' | 'Lvwg' | 'Bfg'
  | 'Dsk' | 'Dok' | 'Pvak' | 'Gbk' | 'Uvs' | 'AsylGH' | 'Ubas' | 'Umse' | 'Bks' | 'Verg' | 'Ufs'
export type DskAuthority = 'Datenschutzbehoerde'
export type DocumentType = 'RS' | 'TE'
export const COURT_APPLIKATIONEN: Applikation[] = ['Justiz', 'Vfgh', 'Vwgh', 'Bvwg', 'Lvwg', 'Bfg']
export const AUTHORITY_APPLIKATIONEN: Applikation[] = ['Dsk', 'Verg', 'Gbk', 'Pvak', 'Uvs', 'Umse', 'AsylGH', 'Ubas', 'Bks', 'Dok', 'Ufs']

export interface SearchSuggestion {
  label: string
  description: string
  query: string
  paragraph?: string
  gesetz?: string
  keyword?: string
  keywordMode?: 'broad'
  /** Hits of the narrower search, when the server counted them. */
  total?: number
}

export type SearchContent = 'decisions' | 'laws' | 'all'
export const DEFAULT_SEARCH_CONTENT: SearchContent = 'all'

export interface SearchInput {
  /** Explicit partial keyword matching, selected from the empty search state. */
  keywordMode?: 'broad'
  /** Selected catalogue identity, when opening search from a law provision. */
  lawKey?: string
  /** Full-text refinement within the original search, before pagination. */
  resultText?: string
  /** Additional norm restriction within the original result inventory. */
  resultNorm?: string
  legalArea?: string
  content?: SearchContent
  /** Canonical: raw user query — backend parses it. */
  query?: string
  /** Legacy parsed fields, accepted for backwards compat with old links. */
  paragraph?: string
  gesetz?: string
  keyword?: string
  dateFrom?: string
  dateTo?: string
  caseNumber?: string
  ecli?: string
  courts?: Applikation[]
  dskAuthority?: DskAuthority
  /** Internal local-search pagination fields. */
  cursor?: string
  pageSize?: number
  /** Restricts a result page to one RIS document inventory. */
  year?: number
  documentType?: DocumentType
  /** Exact court/authority label from the result facets, not a RIS application. */
  resultCourt?: string
}

export interface SearchFacets {
  rs: number
  te: number
  courts: number
  direct: number
  linked: number
  courtAliases?: Record<string, string>
  courtCounts?: CourtDecisionCountMap
}

export interface SearchResult {
  results: RisDocument[]
  total?: number
  nextCursor: string | null
  facets?: SearchFacets
  log: string[]
  otherCourts: CourtProbe[]
  suggestions: SearchSuggestion[]
  /** Set to the keyword when the search dropped it to surface bare norm hits. */
  keywordRelaxed?: string
}

function toRisDocument(doc: SearchBackendDocument): RisDocument {
  return {
    id: doc.id,
    typ: doc.typ,
    gz: doc.gz,
    rechtssatznummer: doc.rechtssatznummer || '',
    gericht: doc.gericht,
    courtDisplayName: doc.courtDisplayName,
    datum: doc.datum,
    normen: doc.normen,
    ecli: doc.ecli,
    url: doc.url,
    htmlUrl: doc.htmlUrl,
    anmerkung: doc.anmerkung || '',
    reason: doc.reason,
    searchScore: doc.searchScore,
    schlagworte: doc.schlagworte || '',
    kurzinformation: doc.kurzinformation || '',
    entscheidungsart: doc.entscheidungsart || '',
    geaendert: doc.geaendert || '',
    linkedFromIds: doc.linkedFromIds || [],
    caseMatch: doc.caseMatch,
    searchExcerpts: doc.searchExcerpts,
    searchHighlightTerms: doc.searchHighlightTerms,
    furtherMentionCount: doc.furtherMentionCount,
    searchHeading: doc.searchHeading,
    excerptLinkedMatch: doc.excerptLinkedMatch,
    excerptStatus: doc.excerptStatus,
    eugh: doc.eugh || [],
    deepScanned: false,
    analysisAvailable: doc.analysisAvailable || false,
    volltextNormen: doc.volltextNormen,
    citations: doc.citations,
    eughContexts: doc.eughContexts,
  }
}

export async function searchBackend(
  input: SearchInput,
  signal?: AbortSignal,
): Promise<SearchResult> {
  const res = await apiFetch('/api/search', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
    signal,
  })

  const data = await readSearchResponse(res)

  const results = (data.results || []).map(doc => ({ ...toRisDocument(doc), referenceCatalog: data.referenceCatalog }))

  return {
    results,
    total: data.total,
    nextCursor: data.nextCursor || null,
    facets: data.facets ? {
      rs: data.facets?.rs ?? results.filter(result => result.typ === 'RS').length,
      te: data.facets?.te ?? results.filter(result => result.typ === 'TE').length,
      courts: data.facets?.courts ?? new Set(results.map(result => result.gericht).filter(Boolean)).size,
      direct: data.facets?.direct ?? results.filter(result => result.reason !== 'verknuepft').length,
      linked: data.facets?.linked ?? results.filter(result => result.reason === 'verknuepft').length,
      courtCounts: data.facets?.courtCounts,
    } : undefined,
    log: data.log || [],
    otherCourts: data.otherCourts || [],
    suggestions: data.suggestions || [],
    keywordRelaxed: data.keywordRelaxed,
  }
}

export class SearchTimeoutError extends Error {
  constructor() {
    super('Die Suche hat das Zeitlimit erreicht. Die Treffer konnten nicht geladen werden. Bitte versuche es erneut oder grenze die Suche ein.')
    this.name = 'SearchTimeoutError'
  }
}

async function readSearchResponse(response: Response): Promise<SearchBackendResponse> {
  if (!response.ok) {
    const error = await response.json().catch(() => null) as SearchBackendResponse | null
    if (error?.timedOut) throw new SearchTimeoutError()
    throw new Error(`Search failed: ${response.status}`)
  }
  const data = await response.json() as SearchBackendResponse
  if (data.timedOut) throw new SearchTimeoutError()
  return data
}

export interface SearchYear {
  year: number
  rs: number
  te: number
  eugh: number
  total: number
}

export interface SearchSummaryResult {
  years?: SearchYear[]
  total: number | null
  facets: SearchFacets | null
  otherCourts?: CourtProbe[]
}

export async function searchSummaryBackend(
  input: SearchInput,
  signal?: AbortSignal,
): Promise<SearchSummaryResult> {
  const res = await apiFetch('/api/search/summary', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
    signal,
  })
  if (!res.ok) throw new Error(`Search summary failed: ${res.status}`)
  return await res.json() as SearchSummaryResult
}

/** Full search for a specific court system */
export async function searchCourt(
  input: SearchInput & { applikation: Applikation },
  signal?: AbortSignal,
): Promise<RisDocument[]> {
  const res = await apiFetch('/api/search/court', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...input, pageSize: 500 }),
    signal,
  })

  const data = await readSearchResponse(res)
  return (data.results || []).map((doc) => ({
    referenceCatalog: data.referenceCatalog,
    id: doc.id,
    typ: doc.typ,
    gz: doc.gz,
    rechtssatznummer: doc.rechtssatznummer || '',
    gericht: doc.gericht,
    courtDisplayName: doc.courtDisplayName,
    datum: doc.datum,
    normen: doc.normen,
    ecli: doc.ecli,
    url: doc.url,
    htmlUrl: doc.htmlUrl,
    anmerkung: doc.anmerkung || '',
    reason: doc.reason,
    searchScore: doc.searchScore,
    schlagworte: doc.schlagworte || '',
    kurzinformation: doc.kurzinformation || '',
    entscheidungsart: doc.entscheidungsart || '',
    geaendert: doc.geaendert || '',
    linkedFromIds: doc.linkedFromIds || [],
    caseMatch: doc.caseMatch,
    searchExcerpts: doc.searchExcerpts,
    searchHighlightTerms: doc.searchHighlightTerms,
    furtherMentionCount: doc.furtherMentionCount,
    searchHeading: doc.searchHeading,
    excerptLinkedMatch: doc.excerptLinkedMatch,
    excerptStatus: doc.excerptStatus,
    eugh: doc.eugh || [],
    deepScanned: false,
    analysisAvailable: doc.analysisAvailable || false,
    volltextNormen: doc.volltextNormen,
    citations: doc.citations,
    eughContexts: doc.eughContexts,
  }))
}

export interface DeepScanMeta {
  referenceCatalog?: import('../data/gesetze').Gesetz[] | null
  eughRefs: string[]
  eughContexts: Record<string, string>
  relevance: number
  volltextNormen: string[]
  citations: string[]
}

export interface EughAnalysisResult {
  referenceCatalog?: import('../data/gesetze').Gesetz[] | null
  /** Every result of the search citing the EuGH, across all result pages. */
  results: RisDocument[]
  total: number
  unanalyzed: number
  truncated: boolean
  cases: EughCaseMatch[]
  /** Cases whose metadata is still being fetched upstream by the server. */
  pendingCaseIds: string[]
}

export async function fetchEughAnalysis(input: SearchInput, locale: string, signal?: AbortSignal): Promise<EughAnalysisResult> {
  const res = await apiFetch('/api/search/eugh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...input, cursor: undefined, pageSize: undefined, locale }),
    signal,
  })
  if (!res.ok) throw new Error(`EuGH analysis failed: ${res.status}`)
  const data = await res.json() as Omit<EughAnalysisResult, 'results'> & { results?: SearchBackendDocument[] }
  return {
    results: (data.results || []).map(doc => ({ ...toRisDocument(doc), referenceCatalog: data.referenceCatalog })),
    total: data.total,
    unanalyzed: data.unanalyzed,
    truncated: data.truncated,
    cases: data.cases || [],
    pendingCaseIds: data.pendingCaseIds || [],
  }
}

export type DeepScanResults = Record<string, DeepScanMeta>

export const DEEP_SCAN_LIMIT = 12
const DEEP_SCAN_POLL_TIMEOUT_MS = 45_000

interface DeepScanResponse {
  results?: DeepScanResults
  pending?: string[]
  failed?: string[]
  complete?: boolean
  retryAfterSeconds?: number
}

function deepScanDelay(ms: number, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const cleanup = () => signal?.removeEventListener('abort', abort)
    const timeoutId = setTimeout(() => {
      cleanup()
      resolve()
    }, ms)
    const abort = () => {
      clearTimeout(timeoutId)
      cleanup()
      reject(new DOMException('Aborted', 'AbortError'))
    }
    if (signal?.aborted) abort()
    else signal?.addEventListener('abort', abort, { once: true })
  })
}

export async function deepScanBackend(
  docs: RisDocument[],
  paragraph: string,
  gesetz: string,
  signal?: AbortSignal,
): Promise<DeepScanResults> {
  const tes = docs.filter(d => d.typ === 'TE' && !d.deepScanned).slice(0, DEEP_SCAN_LIMIT)
  if (tes.length === 0) return {}

  const startedAt = Date.now()
  const results: DeepScanResults = {}
  while (!signal?.aborted) {
    const res = await apiFetch('/api/deep-scan', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        docs: tes.map(d => ({ docId: d.id, gz: d.gz })),
        paragraph,
        gesetz,
      }),
      signal,
    })

    if (!res.ok) return results

    const data = await res.json() as DeepScanResponse
    Object.assign(results, data.results || {})
    if (data.complete !== false || Date.now() - startedAt >= DEEP_SCAN_POLL_TIMEOUT_MS) return results

    const delayMs = Math.min(Math.max((data.retryAfterSeconds || 1) * 1_000, 500), 2_000)
    await deepScanDelay(delayMs, signal)
  }

  return results
}

export interface DecisionTextRun {
  text: string
  bold?: boolean
  italic?: boolean
  underline?: boolean
  superscript?: boolean
  subscript?: boolean
  strikethrough?: boolean
  href?: string
  style?: DecisionStyle
}

export interface DecisionStyle {
  backgroundColor?: string
  border?: string
  borderTop?: string
  borderRight?: string
  borderBottom?: string
  borderLeft?: string
  borderCollapse?: string
  breakInside?: string
  breakBefore?: string
  breakAfter?: string
  color?: string
  fontFamily?: string
  fontSize?: string
  fontStyle?: string
  fontWeight?: string
  height?: string
  letterSpacing?: string
  lineHeight?: string
  marginTop?: string
  marginRight?: string
  marginBottom?: string
  marginLeft?: string
  maxWidth?: string
  minWidth?: string
  padding?: string
  paddingTop?: string
  paddingRight?: string
  paddingBottom?: string
  paddingLeft?: string
  textAlign?: string
  textDecoration?: string
  textIndent?: string
  verticalAlign?: string
  width?: string
}

export interface DecisionParagraphBlock {
  type: 'paragraph'
  runs: DecisionTextRun[]
  align?: 'left' | 'center' | 'right' | 'justify'
  style?: DecisionStyle
}

export interface DecisionImageBlock {
  type: 'image'
  src?: string
  data?: string
  mediaType?: string
  filename?: string
  alt?: string
  description?: string
  widthPt?: number
  heightPt?: number
}

export interface DecisionAttachmentBlock {
  type: 'attachment'
  src?: string
  data?: string
  mediaType?: string
  filename?: string
  description?: string
}

export interface DecisionTableCell {
  blocks: DecisionContentBlock[]
  colSpan?: number
  rowSpan?: number
  header?: boolean
  style?: DecisionStyle
}

export interface DecisionTableBlock {
  type: 'table'
  caption?: string
  style?: DecisionStyle
  rows: Array<{ cells: DecisionTableCell[]; style?: DecisionStyle }>
}

export interface DecisionListBlock {
  type: 'list'
  items: Array<{ marker?: string; blocks: DecisionContentBlock[] }>
}

export interface DecisionHorizontalRuleBlock {
  type: 'horizontalRule'
  align?: 'left' | 'center' | 'right' | 'justify'
  style?: DecisionStyle
}

export interface DecisionBreakBlock {
  type: 'break'
  kind: 'page' | 'column' | 'section'
}

export interface DecisionSpacerBlock {
  type: 'spacer'
}

export type DecisionContentBlock =
  | DecisionParagraphBlock
  | DecisionImageBlock
  | DecisionAttachmentBlock
  | DecisionTableBlock
  | DecisionListBlock
  | DecisionHorizontalRuleBlock
  | DecisionBreakBlock
  | DecisionSpacerBlock
export type DecisionRichSection = 'kopf' | 'spruch' | 'sachverhalt' | 'text' | 'rechtlicheBeurteilung' | 'rechtssatz'

export interface DecisionReferenceSource {
  caseNumber: string
  court: string
  date: string
  sourceStatus: 'linked' | 'unlinked' | 'unknown'
  documentId?: string | null
  sourceUrl?: string | null
}

export interface LinkedDecisionEntry {
  noteNumbers?: number[]
  sourceIndex: number
  unparsed?: boolean
  header?: string
  court?: string
  date?: string
  caseNumber?: string
  documentId?: string | null
  paragraphs: string[]
  publications?: string[]
}

export interface StructuredDecision {
  referenceCatalog?: import('../data/gesetze').Gesetz[] | null
  typ: 'RS' | 'TE'
  gericht: string
  courtDisplayName?: string
  entscheidungsdatum: string
  gz: string
  ecli: string
  kopf: string[]
  spruch: string[]
  sachverhalt: string[]
  text?: string[]
  rechtlicheBeurteilung: string[]
  rechtssatznummer: string
  normen: string[]
  schlagworte?: string
  originDecisionUrl?: string | null
  rechtssatz: string[]
  entscheidungstexte?: string[]
  decisionReferences?: DecisionReferenceSource[]
  linkedDecisions?: { entries: LinkedDecisionEntry[]; warnings: { index: number; reason: string }[] }
  additionalSections?: { key: string; label: string; content: string[]; richContent?: DecisionContentBlock[] }[]
  richContent?: Partial<Record<DecisionRichSection, DecisionContentBlock[]>>
}

export interface VolltextResponse {
  status: 'loading' | 'ready' | 'failed' | 'missing'
  structured: StructuredDecision | null
  html: string
  error?: string
  sourceUrl?: string
  retryAfterSeconds?: number
}

const VOLLTEXT_TIMEOUT_MS = 20_000

function volltextRequestSignal(signal?: AbortSignal) {
  const controller = new AbortController()
  const forwardAbort = () => controller.abort(signal?.reason)
  const timeoutId = setTimeout(() => controller.abort(), VOLLTEXT_TIMEOUT_MS)

  if (signal?.aborted) forwardAbort()
  else signal?.addEventListener('abort', forwardAbort, { once: true })

  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timeoutId)
      signal?.removeEventListener('abort', forwardAbort)
    },
  }
}

export async function fetchDocVolltext(docId: string, signal?: AbortSignal): Promise<VolltextResponse> {
  const request = volltextRequestSignal(signal)
  try {
    while (!request.signal.aborted) {
      const res = await apiFetch(`/api/ris/documents/${encodeURIComponent(docId)}/content`, { signal: request.signal })
      const payload = await res.json() as VolltextResponse
      if (payload.status !== 'loading') return payload

      const delayMs = Math.min(Math.max((payload.retryAfterSeconds || 1) * 1_000, 500), 1_500)
      await new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(resolve, delayMs)
        request.signal.addEventListener('abort', () => {
          clearTimeout(timeoutId)
          reject(new DOMException('Aborted', 'AbortError'))
        }, { once: true })
      })
    }

    throw new DOMException('Aborted', 'AbortError')
  } catch (error) {
    const abortedByCaller = signal?.aborted
    return {
      status: 'failed',
      structured: null,
      html: '',
      error: abortedByCaller
        ? 'Dokumentabruf wurde abgebrochen.'
        : 'Der strukturierte Inhalt konnte nicht rechtzeitig geladen werden.',
    }
  } finally {
    request.cleanup()
  }
}

// --- Law Text ---

export interface LawAbsatz {
  sourceType?: string
  anchor?: string
  nummer: string   // "(1)", "(2)", or ""
  text: string
  subsection?: string
  kind?: 'heading' | 'paragraph' | 'list'
  listLevel?: number
  richContent?: DecisionContentBlock[]
}

export interface StructuredLawText {
  kurztitel: string
  kundmachungsorgan: string
  typ: string
  paragraphLabel: string
  inkrafttretensdatum: string
  ausserkrafttretensdatum: string
  index: string
  gesetzesnummer: string
  dokumentnummer: string
  absaetze: LawAbsatz[]
  additionalSections?: { key: string; label: string; content: string[]; blocks?: LawAbsatz[] }[]
}

export interface LawVersion {
  referenceCatalog?: import('../data/gesetze').Gesetz[] | null
  risAbfrage?: string
  id: string
  inkraftAb: string
  ausserkraftAb: string
  structured: StructuredLawText
  plainText: string
}

export interface LawProvision {
  key: string
  label: string
  versions: LawVersion[]
  currentVersionId: string | null
}

export interface LawTextResponse {
  referenceCatalog?: import('../data/gesetze').Gesetz[] | null
  versions: LawVersion[]
  currentVersionId: string | null
  provisions?: LawProvision[]
  status: 'ready' | 'pending' | 'missing' | 'failed'
}

const LAW_TEXT_TIMEOUT_MS = 5_000

export async function fetchLawText(
  gesetzesnummer: string,
  paragraph: string,
  signal?: AbortSignal,
): Promise<LawTextResponse> {
  const controller = new AbortController()
  const forwardAbort = () => controller.abort(signal?.reason)
  const timeoutId = setTimeout(() => controller.abort(), LAW_TEXT_TIMEOUT_MS)

  if (signal?.aborted) forwardAbort()
  else signal?.addEventListener('abort', forwardAbort, { once: true })

  try {
    const params = new URLSearchParams({ gesetzesnummer, paragraph })
    const res = await apiFetch(`/api/law-text?${params}`, { signal: controller.signal })
    if (!res.ok) return { versions: [], currentVersionId: null, status: 'failed' }
    const data = await res.json() as LawTextResponse
    const withContext = (version: LawVersion) => ({ ...version, referenceCatalog: data.referenceCatalog })
    return { ...data, versions: data.versions.map(withContext),
      provisions: data.provisions?.map(provision => ({ ...provision, versions: provision.versions.map(withContext) })) }
  } catch {
    return { versions: [], currentVersionId: null, status: 'failed' }
  } finally {
    clearTimeout(timeoutId)
    signal?.removeEventListener('abort', forwardAbort)
  }
}

// --- Top Searches ---

export interface TopSearch {
  paragraph: string
  gesetz: string
}

export async function fetchTopSearches(): Promise<TopSearch[]> {
  try {
    const res = await apiFetch('/api/search/top')
    if (!res.ok) return []
    return await res.json() as TopSearch[]
  } catch {
    return []
  }
}
